import { useEffect } from 'react'

import type { AdjustPaymentTarget } from '../AdjustPaymentModal.types'
import type { ReviewQueueFilter } from '../../../api/reconciliation-v2-api-client'
import { resolveOpenTarget } from './row-targets'
import type { ReviewQueuePanelState } from './useReviewQueuePanel'

/**
 * Open the Adjust Payment modal on the single row an inbound `rqOpen` names,
 * then consume the one-shot.
 *
 * The deep link exists so an operator can send a colleague one item rather
 * than a tab and directions to it. Mirrors `tdOpen` on Transaction Details and
 * the `adj*` pair on Benefits: seed the list so the row is reachable (done by
 * ./url-state on mount), wait for it, open it, clear the key.
 *
 * Identical on both tabs — `resolveOpenTarget` discriminates the arm off
 * the target itself — so this is one hook rather than two copies of the
 * effect.
 */
export const useRowDeepLink = <F extends ReviewQueueFilter>(
  panel: ReviewQueuePanelState<F>,
  /**
   * MUST be stable across renders — the shell passes its `setAdjustTarget`
   * straight through, and it sits in the dep list below rather than behind a
   * ref on that basis. An inline arrow here would re-run the effect every
   * render; the early return makes that harmless only AFTER the one-shot is
   * consumed.
   */
  onOpenTarget: (target: AdjustPaymentTarget) => void
): void => {
  const { pendingOpen, items, consumeOpen } = panel
  useEffect(() => {
    // `undefined` until the page this link seeded has actually settled — see
    // ReviewQueuePanelState.pendingOpen.
    if (!pendingOpen) return
    const target = resolveOpenTarget(items, pendingOpen)
    if (target) onOpenTarget(target)
    // Consumed either way. Not finding the row is an ordinary outcome — the
    // work may already be cleared — and a link that stays in the URL would
    // re-fire on every reload and pin the list to a search for a row that is
    // not coming back.
    consumeOpen()
  }, [pendingOpen, items, consumeOpen, onOpenTarget])
}
